import Image from "next/image";
import type { ReactNode } from "react";
import { APP_NAME, COMPANY } from "@/constants";

export interface StatusPageProps {
  /** The chip beside the logo, e.g. "404 · Page not found". */
  code: string;
  title: ReactNode;
  /** The explanation under the title. */
  description: ReactNode;
  /** Everything below: address boxes, buttons, tips, footer. */
  children?: ReactNode;
  /** The name beside the logo; the app's by default, the company's on the website. */
  brand?: { name: string; caption: string };
}

/**
 * Full-screen status page (403 wrong address, 404 not found…) in the login
 * screen's look: the building photo under the brand red, one white card.
 * Fixed to the window, because the root layout adds elements after the page
 * and a backdrop sized to the content would leave a blank band beneath it.
 */
export function StatusPage({
  code,
  title,
  description,
  children,
  brand = { name: APP_NAME, caption: COMPANY.name },
}: StatusPageProps) {
  return (
    <main className="fixed inset-0 z-40 overflow-y-auto">
      <Image src="/images_auth/building.webp" alt="" fill priority className="object-cover" />
      <div className="fixed inset-0 bg-gradient-to-br from-[#bf0302]/95 via-[#a00202]/95 to-[#8f0202]/95" />
      <div className="fixed -left-24 top-1/4 h-96 w-96 rounded-full bg-white/5 blur-3xl" aria-hidden />
      <div className="fixed -right-24 bottom-1/4 h-96 w-96 rounded-full bg-white/5 blur-3xl" aria-hidden />

      <div className="relative flex min-h-full items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8 dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <Image src="/icons/icon-192.png" alt="" width={40} height={40} className="rounded-lg" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{brand.name}</p>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{brand.caption}</p>
            </div>
            <span className="ml-auto shrink-0 rounded-full bg-[#bf0302]/10 px-2.5 py-1 text-[11px] font-semibold text-[#bf0302] dark:bg-red-400/10 dark:text-red-300">
              {code}
            </span>
          </div>

          <h1 className="mt-6 text-xl font-bold leading-snug text-gray-900 sm:text-2xl dark:text-white">{title}</h1>
          <div className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{description}</div>

          {children}
        </div>
      </div>
    </main>
  );
}

/** Button styles shared by status pages, so every one reads the same. */
export const statusPrimaryButton =
  "inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#bf0302] px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#a00202] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0302] focus-visible:ring-offset-2";
export const statusSecondaryButton =
  "inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700";

/** The right-arrow on a status page's primary button. */
export function StatusArrow() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}
